/**
 * Buscador de Registros y del detalle de lista propia («Buscar por título,
 * dirección o barrio»), en el cliente, sobre lo que ya tiene el panel:
 * registros de las cards (getmyrecords si llegó, si no la versión legacy de
 * getmylots / getmycadparcels) + las filas legacy de origen (`record.source`).
 *
 * - Normaliza acentos, mayúsculas y espacios («cordoba» encuentra «Córdoba»).
 * - Varias palabras: todas tienen que aparecer (AND), en cualquier campo del
 *   mismo registro (p. ej. «depto nueva cordoba» = título de la UF + barrio del lote).
 * - Campos: título y descripción de la publicación (UF, contenido de lote, CAD),
 *   etiqueta / nombre de la UF y Nº de UF catastral, dirección, barrio, localidad,
 *   municipio, departamento y provincia, nombre de parcela CAD / proyecto, tipo de
 *   propiedad (id y etiqueta) y, sin anunciarlo, el número catastral del lote.
 * @see docs/maps/registros-record-cards.md
 */
import {
  getPropertyTypeLabel,
  lotRowToRecords,
  cadParcelToRecord,
} from './recordCards';
import { getDisplayUnits } from './userLotUnits';
import { isCadRegistrosRow } from '../../Showroom/utils/cadRegistros';

/** «  Nueva CÓRDOBA » → «nueva cordoba». */
export const normalizeSearchText = (value) => String(value ?? '')
  .normalize('NFD')
  .replace(/[\u0300-\u036f]/g, '')
  .toLowerCase()
  .replace(/\s+/g, ' ')
  .trim();

/**
 * Término del input → palabras normalizadas (vacío → []). Acepta un array ya parseado.
 * @param {string|string[]|null|undefined} term
 * @returns {string[]}
 */
export const parseSearchTerms = (term) => {
  if (Array.isArray(term)) return term;
  const normalized = normalizeSearchText(term);
  return normalized ? [...new Set(normalized.split(' '))] : [];
};

const textParts = (source, keys) => (source && typeof source === 'object'
  ? keys.map((key) => source[key])
  : []);

/** Dirección / zona de un lote o UF legacy (getmylots). */
const LOCATION_KEYS = [
  'street_name', 'street', 'street_number', 'address',
  'neighborhood_name', 'neighborhood', 'locality_name', 'locality',
  'municipality_name', 'department_name', 'province_name',
];
const PUBLICATION_KEYS = ['title', 'description', 'unit_label'];

const lotContentParts = (lot) => [
  ...textParts(lot?.lot_content, ['title', 'description']),
  ...textParts(lot?.lot_user_content, ['title', 'description']),
];

/** Campos del lote catastral (sin sus UF): catastral, ubicación y contenido. */
const lotParts = (lot) => [
  lot?.lot_name,
  lot?.lot_id,
  // Lo que ya matcheaba el buscador anterior (`lotMatchesSearch`).
  lot?.lot_state,
  lot?.lot_type,
  ...textParts(lot, LOCATION_KEYS),
  ...lotContentParts(lot),
];

const cache = new WeakMap();

/**
 * Texto normalizado en el que se busca un registro (memoizado por objeto).
 * @param {object} record - registro del adaptador (`toRecords` / `mergeApiRecords`)
 * @returns {string}
 */
export function recordSearchText(record) {
  if (!record || typeof record !== 'object') return '';
  if (cache.has(record)) return cache.get(record);
  const loc = record.location || {};
  const { lot = null, unit = null, parcel = null } = record.source || {};
  const isLotRecord = record.record_kind === 'lote' || record.record_kind === 'marker';
  const parts = [
    // Publicación y nombre de la UF / contenido / parcela CAD.
    record.title,
    record.unit_label,
    record.description,
    record.cad_name,
    record.parcel_name,
    record.project?.name,
    record.block,
    record.official_unit?.unit_number,
    record.official_unit?.unit_number ? `uf ${record.official_unit.unit_number}` : null,
    // Tipo de propiedad: id («departamento») y etiqueta.
    record.property_type,
    getPropertyTypeLabel(record.property_type),
    // Ubicación de la card (API o legacy).
    loc.street, loc.neighborhood, loc.locality, loc.municipality, loc.province, loc.label,
    // Catastral (no se anuncia en el placeholder).
    record.lot_name,
    // Filas legacy de origen: descripción de la UF, del contenido y ubicación del lote.
    ...textParts(unit, [...PUBLICATION_KEYS, ...LOCATION_KEYS]),
    ...(lot ? textParts(lot, ['lot_name', 'lot_state', 'lot_type', ...LOCATION_KEYS]) : []),
    ...(isLotRecord && lot ? lotContentParts(lot) : []),
    ...textParts(parcel, ['title', 'name', 'project_name', 'description']),
  ];
  const out = normalizeSearchText(parts.filter((p) => p != null && p !== '').join(' '));
  cache.set(record, out);
  return out;
}

const matchesAll = (haystack, terms) => terms.every((t) => haystack.includes(t));

/**
 * ¿El registro tiene todas las palabras? Sin término → true.
 * @param {object} record
 * @param {string|string[]} term
 */
export function recordMatchesSearch(record, term) {
  const terms = parseSearchTerms(term);
  if (!terms.length) return true;
  return matchesAll(recordSearchText(record), terms);
}

/** Item de card (`toRecordCard`): busca en su `record`. */
export const recordItemMatchesSearch = (item, term) => recordMatchesSearch(item?.record || item, term);

/**
 * Vista agrupada: el lote se muestra si matchea él (catastral, ubicación,
 * contenido) o alguno de sus registros (UF / contenido). Las palabras tienen que
 * estar todas en el lote o todas en un mismo registro (que ya incluye la
 * ubicación del lote). Parcelas CAD: su registro.
 * @param {object} row - fila de getmylots o CAD normalizada
 * @param {string|string[]} term
 */
export function lotRowMatchesSearch(row, term) {
  const terms = parseSearchTerms(term);
  if (!terms.length) return true;
  if (!row) return false;
  if (isCadRegistrosRow(row)) return recordMatchesSearch(cadParcelToRecord(row), terms);
  const lotText = normalizeSearchText(lotParts(row).filter(Boolean).join(' '));
  if (matchesAll(lotText, terms)) return true;
  const records = lotRowToRecords(row, { includeMarkers: true });
  if (records.some((record) => recordMatchesSearch(record, terms))) return true;
  // UF sin id (no generan registro): sus textos con la ubicación del lote.
  return getDisplayUnits(row).some((unit) => matchesAll(
    normalizeSearchText([lotText, ...textParts(unit, PUBLICATION_KEYS)].filter(Boolean).join(' ')),
    terms,
  ));
}
